<script setup>
import { computed, reactive, ref, watch } from 'vue'
import StationScopeStep from './StationScopeStep.vue'
import { useI18n } from '../../i18n'
import { systemRoleName } from '../../access/labels.js'

const props = defineProps({
  user: { type: Object, default: null }, roles: { type: Array, required: true }, stations: { type: Array, required: true }, saving: { type: Boolean, default: false },
})
const emit = defineEmits(['submit', 'cancel', 'dirty'])
const step = ref(1)
const errors = ref([])
const { t } = useI18n()
const stepLabels = computed(() => [t('accountStep'), t('roleStep'), t('stationsStep'), t('reviewStep')])
const form = reactive({
  username: props.user?.username ?? '', displayName: props.user?.displayName ?? '', initialPassword: '',
  roleId: props.user?.role?.id ?? props.roles[0]?.id ?? '',
  physicalStationIds: props.user?.physicalStationIds ? [...props.user.physicalStationIds] : [],
  naftaPosInstanceIds: props.user?.naftaPosInstanceIds ? [...props.user.naftaPosInstanceIds] : [...(props.user?.stationIds ?? [])],
  pts2ControllerIds: props.user?.pts2ControllerIds ? [...props.user.pts2ControllerIds] : [],
})
const selectedRole = computed(() => props.roles.find((role) => Number(role.id) === Number(form.roleId)))
const administrator = computed(() => selectedRole.value?.systemCode === 'ADMIN')
const pts2ServiceEnabled = computed(() => administrator.value
  || selectedRole.value?.effectivePermissionKeys?.includes('pts2.application.use'))
const stationNames = computed(() => props.stations.filter((station) => form.physicalStationIds.includes(station.id)).map((station) => station.displayName))

watch(form, () => { emit('dirty', true) }, { deep: true })
watch(administrator, (value) => {
  if (value) {
    form.physicalStationIds = []
    form.naftaPosInstanceIds = []
    form.pts2ControllerIds = []
  }
})
watch(pts2ServiceEnabled, (value) => {
  if (!value) form.pts2ControllerIds = []
})

function validate(current = step.value) {
  errors.value = []
  if (current === 1) {
    if (!props.user && !/^[A-Za-z0-9._-]{3,64}$/.test(form.username)) errors.value.push(t('invalidUsername'))
    if (!form.displayName.trim()) errors.value.push(t('requiredFullName'))
    if (!props.user && form.initialPassword.length < 10) errors.value.push(t('shortInitialPassword'))
  }
  if (current === 2 && !selectedRole.value) errors.value.push(t('chooseRole'))
  return errors.value.length === 0
}

function next() {
  if (!validate()) return
  step.value = Math.min(4, step.value + 1)
}

function back() {
  step.value = Math.max(1, step.value - 1)
}

function submit() {
  if (!validate(1) || !validate(2)) return
  const sourceIds = administrator.value ? [] : form.naftaPosInstanceIds
  const controllerIds = administrator.value || !pts2ServiceEnabled.value ? [] : form.pts2ControllerIds
  emit('submit', {
    ...form,
    roleId: Number(form.roleId),
    physicalStationIds: administrator.value ? [] : form.physicalStationIds,
    naftaPosInstanceIds: sourceIds,
    pts2ControllerIds: controllerIds,
    stationIds: sourceIds,
  })
}
</script>

<template>
  <div class="access-wizard">
    <div class="wizard-step-labels"><button v-for="(label, index) in stepLabels" :key="label" type="button" :class="{ active: step === index + 1, done: step > index + 1 }" @click="step = index + 1"><span>{{ step > index + 1 ? '✓' : index + 1 }}</span>{{ label }}</button></div>
    <p v-if="errors.length" class="notice error" role="alert"><strong>{{ t('validationSummary') }}</strong><span v-for="error in errors" :key="error">{{ error }}</span></p>
    <section v-if="step === 1" class="wizard-card"><h2>{{ t('accountStep') }}</h2><p>{{ t('accountStepHint') }}</p>
      <div class="wizard-form-grid"><label>{{ t('username') }}<input v-model.trim="form.username" :disabled="Boolean(user)" maxlength="64" autocomplete="username" /></label><label>{{ t('fullName') }}<input v-model.trim="form.displayName" maxlength="120" autocomplete="name" /></label><label v-if="!user" class="wide">{{ t('initialPassword') }}<input v-model="form.initialPassword" type="password" minlength="10" autocomplete="new-password" /><small>{{ t('initialPasswordHint') }}</small></label></div>
    </section>
    <section v-else-if="step === 2" class="wizard-card"><h2>{{ t('roleStep') }}</h2><p>{{ t('roleStepHint') }}</p><div class="role-choice-grid"><label v-for="role in roles" :key="role.id" :class="{ selected: Number(form.roleId) === role.id }"><input v-model="form.roleId" type="radio" :value="role.id" /><span><strong>{{ systemRoleName(t, role) }}</strong><small>{{ role.isSystem ? t('systemRole') : (role.description || t('customRole')) }}</small></span><em>{{ role.isSystem ? t('systemBadge') : t('customBadge') }}</em></label></div></section>
    <section v-else-if="step === 3" class="wizard-card"><h2>{{ t('chooseStations') }}</h2><p>{{ t('chooseStationsHint') }}</p><StationScopeStep v-model="form.physicalStationIds" v-model:source-model-value="form.naftaPosInstanceIds" v-model:pts2-model-value="form.pts2ControllerIds" :stations="stations" :all="administrator" :pts2-enabled="pts2ServiceEnabled" /></section>
    <section v-else class="wizard-card"><h2>{{ t('reviewBeforeSave') }}</h2><p>{{ t('reviewHint') }}</p><dl class="wizard-review"><dt>{{ t('username') }}</dt><dd>{{ form.username }}</dd><dt>{{ t('fullName') }}</dt><dd>{{ form.displayName }}</dd><dt>{{ t('role') }}</dt><dd><span class="role-chip">{{ systemRoleName(t, selectedRole) }}</span></dd><dt>{{ t('stationAccess') }}</dt><dd>{{ administrator ? t('allStationsFuture') : (stationNames.join(', ') || t('noStationAccess')) }}</dd><dt>{{ t('naftaPosWorkplaces') }}</dt><dd>{{ administrator ? t('allStationsFuture') : form.naftaPosInstanceIds.length }}</dd><dt>{{ t('pts2Controllers') }}</dt><dd>{{ administrator ? t('allStationsFuture') : form.pts2ControllerIds.length }}</dd></dl></section>
    <footer class="wizard-actions"><button class="button button-secondary" type="button" @click="step === 1 ? emit('cancel') : back()">{{ step === 1 ? t('cancel') : t('back') }}</button><button v-if="step < 4" class="button access-primary" type="button" @click="next">{{ t('continueAction') }}</button><button v-else class="button access-primary" type="button" :disabled="saving" @click="submit">{{ saving ? t('savingAction') : (user ? t('saveChanges') : t('createUserAction')) }}</button></footer>
  </div>
</template>
